// src/pages/ChatBot/query-logs/InspectModal.tsx
//
// Read-only inspector for a logged chatbot query: what was asked, what was
// served, and the full retrieval trace. Fetches the detail doc when queryId
// becomes non-null and renders the query card, answer card, and the
// stage-lens trace explorer (inspect-trace.tsx).

import { useEffect, useMemo, useState } from 'react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { useModalBackdropClose } from '../../../hooks/useModalBackdropClose';
import { fetchQueryLog, QueryLogApiError } from './api';
import { deriveQueryStatus, deriveQueryFlags } from './derive';
import { statusTone, flagTone } from './chip-tones';
import Tag from '../shared/Tag';
import type { QueryLogDetailResponse, QueryLogDoc } from './types';
import { resolveSearchTerms } from './inspect-trace-helpers';
import { AnswerCard, QueryCard } from './inspect-cards';
import InspectStats from './inspect-stats';
import TraceExplorer from './inspect-trace';
import './InspectModal.css';

interface InspectModalProps {
  queryId: string | null;
  onClose: () => void;
}

type LoadState =
  | { phase: 'idle' }
  | { phase: 'loading'; queryId: string }
  | { phase: 'error'; queryId: string; message: string }
  | { phase: 'loaded'; queryId: string; detail: QueryLogDetailResponse };

export default function InspectModal({ queryId, onClose }: InspectModalProps) {
  const backdropHandlers = useModalBackdropClose(onClose);
  useEscapeClose(onClose, queryId !== null);
  const [loadState, setLoadState] = useState<LoadState>({ phase: 'idle' });

  useEffect(() => {
    if (queryId === null) {
      setLoadState({ phase: 'idle' });
      return undefined;
    }
    let isCancelled = false;
    setLoadState({ phase: 'loading', queryId });
    fetchQueryLog(queryId)
      .then((detail) => {
        if (!isCancelled) setLoadState({ phase: 'loaded', queryId, detail });
      })
      .catch((error: unknown) => {
        if (isCancelled) return;
        const message =
          error instanceof QueryLogApiError ? error.message : 'Failed to load the query log.';
        setLoadState({ phase: 'error', queryId, message });
      });
    return () => {
      isCancelled = true;
    };
  }, [queryId]);

  if (queryId === null) return null;

  // Only trust state produced for the currently open queryId — a fast
  // re-open must not flash the previous query's content.
  const isCurrent = loadState.phase !== 'idle' && loadState.queryId === queryId;
  const detail = isCurrent && loadState.phase === 'loaded' ? loadState.detail : null;
  const errorMessage = isCurrent && loadState.phase === 'error' ? loadState.message : null;
  const isLoading = !detail && !errorMessage;

  return (
    <div className="smrtdocs-modal-overlay" {...backdropHandlers}>
      <div
        className="smrtdocs-modal"
        style={{
          width: 'min(1040px, 95vw)',
          maxHeight: '85dvh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="smrtdocs-modal-header">
          <div className="qli-header-left">
            <h2 style={{ margin: 0, fontSize: '1rem' }}>Inspect Query</h2>
            {detail && <HeaderTags log={detail.log} />}
          </div>
          <button type="button" className="qli-close-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div
          className="smrtdocs-modal-body"
          style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
        >
          {isLoading && (
            <div className="qli-loading">
              <span className="qli-spinner" />
              Loading query…
            </div>
          )}
          {errorMessage && <div className="qli-error-banner">{errorMessage}</div>}
          {detail && <InspectContent detail={detail} />}
        </div>
      </div>
    </div>
  );
}

// Status pill + the query's derived flag chips, surfaced together in the modal header so a query's
// signals (suspicious, fallback, text-search-failed, …) are visible at a glance beside its status.
function HeaderTags({ log }: { log: QueryLogDoc }) {
  const status = deriveQueryStatus(log);
  const flags = deriveQueryFlags(log);
  return (
    <div className="qli-header-tags">
      <Tag tone={statusTone(status.status)}>{status.status}</Tag>
      {flags.map((flag) => (
        <Tag key={flag.flag} tone={flagTone(flag)}>
          {flag.flag}
        </Tag>
      ))}
    </div>
  );
}

// Unmissable banner when the full-text axis silently degraded — results were vector-only for this query.
function TextSearchFailedBanner({ log }: { log: QueryLogDoc }) {
  const notes = log.retrievalNotes;
  if (!notes?.textSearchFailed) return null;
  return (
    <div className="qli-degraded-banner" role="alert">
      <div className="qli-degraded-title">
        ⚠ Full-text search failed for this query — results are vector-only
      </div>
      {notes.textSearchError && (
        <div className="qli-degraded-detail">{notes.textSearchError}</div>
      )}
    </div>
  );
}

function InspectContent({ detail }: { detail: QueryLogDetailResponse }) {
  const { log, chunkTexts, chunkTokens } = detail;
  const hasTrace = log.chunks !== undefined && log.chunks.length > 0;
  const searchTerms = useMemo(() => resolveSearchTerms(log), [log]);

  return (
    <>
      <TextSearchFailedBanner log={log} />
      <QueryCard log={log} />
      <AnswerCard log={log} />
      {hasTrace && log.chunks && (
        <InspectStats chunks={log.chunks} chunkTokens={chunkTokens} />
      )}
      <div className="qli-card">
        <div className="qli-micro-label qli-trace-heading">Retrieval trace</div>
        {hasTrace && log.chunks ? (
          <TraceExplorer chunks={log.chunks} chunkTexts={chunkTexts} searchTerms={searchTerms} />
        ) : (
          <div className="qli-trace-empty">
            No retrieval trace — this query was refused before retrieval.
          </div>
        )}
      </div>
    </>
  );
}
